<template>
  <div class="svoi-tabs" :class="styles">
    <label v-if="label" class="label">{{ label }}</label>
    <div v-if="items.length" class="tabs">
      <div
        v-for="tab in items"
        :key="isSimple ? tab : tab[trackBy]"
        :class="{ tab: true, '-active': isActiveTab(tab) }"
        @click="onClickTab(tab)"
      >
        {{ isSimple ? tab : tab[itemLabel] }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentPropsOptions, computed, defineComponent, isVue2, PropType } from 'vue-demi'
import { TabItem, TabItemKey, TabItems, Radius, Theme, RadiusEnum, ThemeEnum } from '@svoi-ui/interfaces/tabs'
import { isString, isUnknownObject } from '@svoi-ui/shared/utils/guards'

type ValueType = TabItem | string | Array<TabItem> | Array<string> | null

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: [String, Object, Array] as PropType<ValueType>,
        default: null
      }
    }
  : {
      modelValue: {
        type: [String, Object, Array] as PropType<ValueType>,
        default: null
      }
    }

export default defineComponent({
  name: 'SvoiTabs',
  props: {
    ...modelProp,
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array as PropType<TabItems>,
      default: () => []
    },
    // Label from option Object, that will be visible in the dropdown.
    itemLabel: {
      type: String,
      default: 'title'
    },
    // Key to compare objects
    trackBy: {
      type: [String, Number] as PropType<TabItemKey>,
      default: 'id'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    clearIfSelected: {
      type: Boolean,
      default: false
    },
    borderRadius: {
      type: String as PropType<Radius | string>,
      default: RadiusEnum.m,
      validator: (value: unknown) => {
        return isString(value) && [RadiusEnum.m.toString(), RadiusEnum.l.toString()].includes(value)
      }
    },
    theme: {
      type: String as PropType<Theme | string>,
      default: ThemeEnum.qiwi,
      validator: (value: unknown) => {
        return isString(value) && [ThemeEnum.qiwi.toString(), ThemeEnum.violet.toString()].includes(value)
      }
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    // @ts-expect-error
    const propValue = computed<ValueType>(() => (isVue2 ? props.value : props.modelValue))
    const isSimple = computed(() => isString(props.items[0]))
    const styles = computed(() => ({
      [`-radius-${props.borderRadius}`]: true,
      [`-theme-${props.theme}`]: true
    }))

    const isActiveTab = (tab: TabItems[0]) => {
      if (isString(tab)) {
        return props.multiple
          ? Array.isArray(propValue.value) && (propValue.value as Array<string>).includes(tab)
          : tab === propValue.value
      } else {
        return props.multiple
          ? Array.isArray(propValue.value) &&
              (propValue.value as Array<TabItem>).find(i => tab[props.trackBy] === i[props.trackBy])
          : isUnknownObject(propValue.value) && tab[props.trackBy] === propValue.value[props.trackBy]
      }
    }

    const onClickTab = (tab: TabItems[0]) => {
      let value: ValueType

      if (props.multiple) {
        const values = new Set([...(Array.isArray(propValue.value) ? propValue.value : [])])
        if (props.clearIfSelected && isActiveTab(tab)) {
          values.delete(tab)
        } else {
          values.add(tab)
        }
        value = [...values] as TabItems
      } else {
        value = props.clearIfSelected && isActiveTab(tab) ? null : tab
      }

      emit(isVue2 ? 'input' : 'update:modelValue', value)
    }

    return { isSimple, styles, isActiveTab, onClickTab }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/tabs.scss';
</style>
